'use client';

import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import Box from '../../atoms/Box/Box';
import Loader from '../../atoms/Loader/Loader';
import Typography from '../../atoms/Typography/Typography';
import Modal from '../Modal/Modal';

import styles from './DocusignViewer.module.scss';

type Theme = 'dark' | 'light';

/**
 * Props for the DocusignViewer component
 */
export type DocusignViewerProps = {
  /** The DocuSign signing URL to render in the iframe */
  url: string;
  /** Test ID for the component */
  dataTestId: string;
  /** Whether the viewer modal is open */
  isOpen: boolean;
  /** Callback when the modal is closed */
  onClose: () => void;
  /** Current theme */
  theme?: Theme;
  /** Title displayed in the modal header */
  title?: string;
  /** Optional className for the iframe container */
  className?: string;
  /** Callback fired when the iframe finishes loading */
  onLoad?: () => void;
  /** Callback fired when a DocuSign event message is received */
  onMessage?: (event: MessageEvent) => void;
  /** i18n labels */
  labels?: {
    /** Modal title text */
    title?: string;
    /** Loading state text */
    loading?: string;
    /** Error state text */
    error?: string;
  };
};

/**
 * DocusignViewer - Renders a DocuSign document inside a modal with an embedded iframe.
 * Handles loading state, error state, and listens for DocuSign postMessage events.
 *
 * @component
 * @param {DocusignViewerProps} props - Component props
 * @returns {React.ReactElement} The DocuSign modal viewer
 *
 * @example
 * <DocusignViewer
 *   url={signingUrl}
 *   dataTestId="docusign-viewer"
 *   isOpen={isDocusignOpen}
 *   onClose={() => setIsDocusignOpen(false)}
 *   onMessage={(event) => handleDocusignEvent(event)}
 * />
 */
const DocusignViewer: React.FC<DocusignViewerProps> = ({
  url,
  dataTestId,
  isOpen,
  onClose,
  theme = 'dark',
  title = 'DocuSign Document',
  className,
  onLoad,
  onMessage,
  labels,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hasInitiallyLoaded = useRef(false);

  /** Reset state when modal opens with a new URL */
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setHasError(false);
      hasInitiallyLoaded.current = false;
    }
  }, [isOpen, url]);

  /**
   * Attach load/error listeners via ref for reliable cross-environment behavior.
   * After the initial load, subsequent load events indicate DocuSign has redirected
   * the iframe (e.g. after the user clicks "Finish"). We synthesize a
   * signing_complete message so consumers can react without relying on postMessage.
   */
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !isOpen) return;

    const handleLoadEvent = () => {
      if (!hasInitiallyLoaded.current) {
        hasInitiallyLoaded.current = true;
        setIsLoading(false);
        onLoad?.();
        return;
      }

      // Subsequent load → DocuSign redirected after user action (e.g. "Finish")
      onMessage?.({ data: 'signing_complete' } as MessageEvent);
    };

    const handleErrorEvent = () => {
      setIsLoading(false);
      setHasError(true);
    };

    iframe.addEventListener('load', handleLoadEvent);
    iframe.addEventListener('error', handleErrorEvent);

    return () => {
      iframe.removeEventListener('load', handleLoadEvent);
      iframe.removeEventListener('error', handleErrorEvent);
    };
  }, [isOpen, onLoad, onMessage]);

  /**
   * Listen for DocuSign postMessage events from the iframe
   */
  useEffect(() => {
    if (!onMessage || !isOpen) return;

    const handleMessage = (event: MessageEvent) => {
      onMessage(event);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onMessage, isOpen]);

  return (
    <Modal
      dataTestId={dataTestId}
      isOpen={isOpen}
      onClose={onClose}
      title={labels?.title ?? title}
      theme={theme}
      size="large"
      closeOnOverlayClick={true}
    >
      <Box className={classnames(styles.content, className)} data-testid={`${dataTestId}-content`}>
        {hasError ? (
          <Box className={styles.errorState} data-testid={`${dataTestId}-error`}>
            <Typography variant="body" size="medium" color="gray-3" theme={theme} align="center">
              {labels?.error ?? 'Failed to load the document. Please try again.'}
            </Typography>
          </Box>
        ) : (
          <>
            {isLoading && (
              <Box className={styles.loaderOverlay} data-testid={`${dataTestId}-loader`}>
                <Loader size={48} theme="light" />
                <Typography variant="body" size="small" color="gray-5" theme="light">
                  {labels?.loading ?? 'Loading document...'}
                </Typography>
              </Box>
            )}

            <iframe
              ref={iframeRef}
              src={url}
              title={title}
              className={classnames(styles.iframe, { [styles.hidden]: isLoading })}
              data-testid={`${dataTestId}-iframe`}
              allow="geolocation"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            />
          </>
        )}
      </Box>
    </Modal>
  );
};

DocusignViewer.displayName = 'DocusignViewer';

export default DocusignViewer;
